export interface FlashProfile {
  environment: string;
  label: string;
  chipFamily: string;
  flashSize: string;
  appOffset: number;
  otadataOffset: number;
  /** Table written at 0x8000. Must match board_build.partitions of the pioenv. */
  partitionTable: string;
}

export const PARTITION_TABLE_OFFSET = 0x8000;

/** Catalog IDs, identical to the pioenv names. See doc/firmware-products.md. */
export const FLASH_PROFILES: FlashProfile[] = [
  {
    environment: 'standard-esp32c6',
    label: 'standard-esp32c6',
    chipFamily: 'ESP32-C6',
    flashSize: '4MB',
    appOffset: 0x10000,
    otadataOffset: 0xe000,
    partitionTable: 'partitions_c6_zigbee.bin',
  },
  {
    environment: 'standard-esp32s3',
    label: 'standard-esp32s3',
    chipFamily: 'ESP32-S3',
    flashSize: '4MB',
    appOffset: 0x10000,
    otadataOffset: 0xe000,
    partitionTable: 'partitions_min_spiffs.bin',
  },
  {
    environment: 'large-esp32s3',
    label: 'large-esp32s3',
    chipFamily: 'ESP32-S3',
    flashSize: '4MB',
    appOffset: 0x10000,
    otadataOffset: 0xe000,
    partitionTable: 'partitions_min_spiffs.bin',
  },
  {
    environment: 'large-esp32p4',
    label: 'large-esp32p4',
    chipFamily: 'ESP32-P4',
    flashSize: '16MB',
    appOffset: 0x10000,
    otadataOffset: 0xe000,
    partitionTable: 'partitions_p4_16mb.bin',
  },
  {
    environment: 'standard-esp32',
    label: 'standard-esp32',
    chipFamily: 'ESP32',
    flashSize: '8MB',
    appOffset: 0x10000,
    otadataOffset: 0xe000,
    partitionTable: 'partitions_esp32_8mb.bin',
  },
];

export function profileForEnvironment(environment: string): FlashProfile | undefined {
  return FLASH_PROFILES.find((p) => p.environment === environment);
}

/** Images left over from an older catalog have no i18n key; show their id. */
export function productLabel(environment: string, translate: (key: string) => string): string {
  const key = `flash.product.${environment}`;
  const label = translate(key);
  return label === key ? environment : label;
}

export function normalizeMac(mac: string): string | null {
  const hex = mac.toUpperCase().replace(/[^0-9A-F]/g, '');
  if (hex.length !== 12) {
    return null;
  }
  return hex.match(/.{2}/g)!.join('-');
}

export function deviceIdsForMac(mac: string): string[] {
  const normalized = normalizeMac(mac);
  if (!normalized) {
    return [];
  }
  const parts = normalized.split('-');
  return [`esp32-${normalized}`, `esp32-${[...parts].reverse().join('-')}`];
}

export function firmwareDeviceId(mac: string): string {
  return deviceIdsForMac(mac)[1] || deviceIdsForMac(mac)[0];
}

export function macFromText(text: string): string | null {
  const match = text.match(/([0-9A-Fa-f]{2}[:-]){5}[0-9A-Fa-f]{2}/);
  return match ? normalizeMac(match[0]) : null;
}

export function minifySettings(raw: string): string {
  const parsed = JSON.parse(raw);
  return JSON.stringify(parsed);
}
